import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@material-ui/core'
import { ClearOutlined } from '@material-ui/icons'
import { Autocomplete } from '@material-ui/lab'
import { DialogCloseSaveButtons } from 'components/Common/DialogActionButtons/DialogCloseSaveButtons'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { WorkingStepIconWithDescription } from 'components/Common/WorkingSteps/WorkingStepIconWithDescription'
import { LocalizedWorkingStepLabel } from 'components/Localization/LocalizedWorkingStepLabel'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { matchSorter } from 'match-sorter'
import { WorkingStepTypeLabel } from 'model/WorkingStepType'
import { TextField } from 'mui-rff'
import { useState } from 'react'
import { Field, Form } from 'react-final-form'
import { useAppSelector } from 'store/configureStore'
import { useAppDispatch } from 'store/configureStore'
import { WorkingStepType } from 'services/APIs/InternalAPI/internal-api.contracts'
import { RootAppState } from 'store/configureStore'
import { Issue } from '../../model/IssueContext'
import { IssuesAPI } from '../../services/APIs/ExternalAPI/IssuesAPI'
import {
  ShowException,
  ShowUserFeedbackForm,
  SuccessMessage,
} from '../../store/Application/appActions'
import { useOrganizationAndProjectContext } from '../../utils/useOrganizationContext'
import './UserFeedback.scss'
import { useAuth0 } from '@auth0/auth0-react'
// import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'

const availableWorkingSteps = Array.from(WorkingStepTypeLabel.keys()).filter(
  (x) =>
    ![
      WorkingStepType.NotInitialized,
      WorkingStepType.Setup,
      WorkingStepType.Unloading,
      WorkingStepType.Loading,
    ].includes(x)
)

export const UserFeedbackForm = () => {
  const dispatch = useAppDispatch()
  const { getLocalizedWorkingStep } = useLocalizedWorkingStepLabel()
  const { user } = useAuth0()

  const { isOpen, issue } = useAppSelector((state: RootAppState) => ({
    isOpen: state.app.userFeedback?.show,
    issue: state.app.userFeedback?.issue,
  }))

  // const rowWorkingSteps = useAppSelector(
  //   projectSelectors.bomItemWorkingStepsSelector({
  //     bomItemId: issue?.context?.rowId,
  //   })
  // )

  const { partyId: organizationId } = useOrganizationAndProjectContext()
  const [isSaving, setIsSaving] = useState(false)

  if (!isOpen) return null

  const clearForm = () => {


    dispatch(
      ShowUserFeedbackForm({
        show: isOpen,
        issue: { ...issue, message: '', tags: [] },
      })
    )
  }

  const handleClose = () => {
    clearForm()
    dispatch(ShowUserFeedbackForm({ show: false, issue: undefined }))
  }

  const handleSend = async (issue: Issue) => {
    issue.context.auth0id = user.sub
    const issuesAPI = new IssuesAPI(organizationId)
    try {
      const resp = await issuesAPI.CreateIssue(issue, (req) =>
        setIsSaving(req.isLoading)
      )

      SuccessMessage(
        'feedback',
        `thanks for your feedback. The issue #${resp.workitemid} will be reviewed by development team`
      )

      // SegmentService.getInstance().track('RecognitionIssueCreated', {
      //   partyId: organizationId,
      //   projectId: issue.context?.projectId,
      //   expectedWorkingStep: issue.tags.join(' | '),
      //   recognizedWorkingSteps: rowWorkingSteps
      //     .filter((x) => x.primaryWorkingStep === x.secondaryWorkingStep)
      //     .map((x) => x.primaryWorkingStep)
      //     .join(' | '),
      //   description: issue.message,
      // })

      handleClose()
    } catch (err) {
      ShowException('user feedback', err)
    } finally {
      setIsSaving(false)
    }
  }

  const validateIssue = (data: Issue) => {
    const errors = {}

    // if (!data.message) {
    // errors['message'] = 'required'
    // }
    if (!data.tags?.length) {
      errors['tags'] = 'required'
    }

    return errors
  }

  return (
    <Dialog
      open={isOpen}
      onClose={(_, reason) => reason !== 'backdropClick' && handleClose()}
      aria-labelledby="form-dialog-title"
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle id="form-dialog-title">
        report a recognition issue
      </DialogTitle>
      <DialogContent className="issueform--body">
        {/* <DialogContentText>
          describe the issue to send to rhodium24 team
        </DialogContentText> */}
        <Form
          initialValues={issue}
          onSubmit={handleSend}
          validate={validateIssue}
          render={(formProps) => {
            return (
              <form onSubmit={formProps.handleSubmit} id="issue-form">
                <Field name="tags">
                  {(fieldProps) => (
                    <Autocomplete
                      multiple
                      options={availableWorkingSteps}
                      disableCloseOnSelect
                      autoHighlight
                      // value={fieldProps.input.value}
                      // value={fieldProps.input.value}
                      onChange={(ev, value) => {
                        if (typeof value === 'string') {
                          return fieldProps.input.value
                        }
                        fieldProps.input.onChange(value)
                        return value
                      }}
                      renderOption={(option, state) => {
                        return (
                          <ListItem component="span">
                            <Checkbox checked={state.selected} />
                            <ListItemAvatar>
                              <WorkingStepIconSvg
                                workingStepType={option}
                                attributes={{ width: '2em' }}
                              />
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <LocalizedWorkingStepLabel
                                  workingStepType={option}
                                />
                              }
                            />
                          </ListItem>
                        )
                      }}
                      filterSelectedOptions
                      filterOptions={(options, state) => {
                        const withLocalization = options.map((x) => ({
                          workingStep: x,
                          localizedLabel: getLocalizedWorkingStep(x),
                        }))

                        const sorted = matchSorter(
                          withLocalization,
                          state.inputValue,
                          { keys: ['localizedLabel'] }
                        )

                        return sorted.map((x) => x.workingStep)
                      }}
                      renderTags={(values, tagProps) => {
                        return (
                          <>
                            {values.map((workingStepType, index) => {
                              const { onDelete, ...rest } = tagProps({
                                index,
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              }) as any

                              return (
                                <div
                                  key={index}
                                  {...rest}
                                  style={{
                                    marginTop: '12px',
                                    position: 'relative',
                                  }}
                                >
                                  <WorkingStepIconWithDescription
                                    key={workingStepType}
                                    workingStepType={workingStepType}
                                  />
                                  <IconButton
                                    onClick={onDelete}
                                    style={{
                                      position: 'absolute',
                                      top: '-.5em',
                                      right: '.5em',
                                      // width: '4px',
                                    }}
                                    size="small"
                                  >
                                    <ClearOutlined fontSize="small" />
                                  </IconButton>
                                </div>
                              )
                            })}
                          </>
                        )
                      }}
                      renderInput={(props) => (
                        <TextField
                          {...props}
                          name="tags"
                          label="expected working steps"
                        />
                      )}
                    />
                  )}
                </Field>

                {/* <Checkboxes
                  label="issue type"
                  name="tags"
                  title="issue type"
                  formGroupProps={{
                    className: 'issue-types',
                  }}
                  data={[
                    {
                      label: 'material price is not correct',
                      value: 'material price',
                    },
                    {
                      label: 'material is not selectable or not found',
                      value: 'material selector',
                    },
                    {
                      label: 'sheet metal was not recognized',
                      value: 'sheet metal',
                    },
                    {
                      label: 'folding was not recognized',
                      value: 'folding',
                    },
                    {
                      label: 'unfolding is not correct',
                      value: 'unfolding',
                    },
                    {
                      label: 'tube not recognized',
                      value: 'tube-not-recognized',
                    },
                    {
                      label: 'other issues',
                      value: 'other',
                    },
                  ]}
                /> */}
                <TextField
                  name="message"
                  // required
                  variant="filled"
                  multiline
                  rows={5}
                  disabled={isSaving}
                  placeholder="in a few words, what was the issue and what is the expected output?"
                  margin="normal"
                />
                <Accordion>
                  <AccordionSummary
                    expandIcon={
                      <FontAwesomeIcon
                        icon={faChevronDown as IconProp}
                        size={'xs'}
                      />
                    }
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                    className="is-marginless"
                  >
                    <ListItem>
                      <ListItemText
                        className="data-summary is-paddingless"
                        primary="data"
                        secondary="data that will be shared with development team"
                      />
                    </ListItem>
                  </AccordionSummary>
                  <AccordionDetails className="is-marginless is-paddingless">
                    <pre>{JSON.stringify(formProps.values)}</pre>
                  </AccordionDetails>
                </Accordion>
              </form>
            )
          }}
        />
      </DialogContent>
      <DialogActions>
        <DialogCloseSaveButtons
          saveButtonFormId="issue-form"
          saving={isSaving}
          onCloseButtonClicked={handleClose}
        />
      </DialogActions>
    </Dialog>
  )
}
